import React from 'react';
import { X, Clock, Users, MapPin, Star, Globe, Building, Calendar } from 'lucide-react';
import type { BusinessOffer, Package } from '../../../types/business';
import Itinerary from '../../Itinerary';
import PackageFormulas from '../../PackageFormulas';
import { useQuery } from '@apollo/client';
import { GET_OFFER_COMPLETE_DETAILS } from '../../../graphql/admin/queries/business';
import { getImageUrl } from '../../../utils/imageUrl';

interface OfferDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  offerId: number;
}

export default function OfferDetailsModal({ isOpen, onClose, offerId }: OfferDetailsModalProps) {
  const { data, loading, error } = useQuery(GET_OFFER_COMPLETE_DETAILS, {
    variables: { offerId },
    skip: !isOpen || !offerId,
    onCompleted: (data) => {
      console.log("Données complètes de l'offre:", data);
    }
  });

  if (!isOpen) return null;
  if (loading) return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
    <div className="bg-white p-6 rounded-lg">
      <p className="text-lg">Chargement des détails...</p>
    </div>
  </div>;
  
  if (error) return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
    <div className="bg-white p-6 rounded-lg">
      <p className="text-lg text-red-600">Erreur: {error.message}</p>
    </div>
  </div>;

  const offer = data?.businessOffer;
  if (!offer) return null;

  const pkg = offer.package;
  if (!pkg) return null;

  // Formater la date de validité
  const validUntil = new Date(offer.validUntil).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Vérifier si les réservations existent et sont un tableau
  const hasBookings = Array.isArray(offer.bookings) && offer.bookings.length > 0;
  
  // Vérifier si les formules existent et sont un tableau
  const hasFormulas = Array.isArray(pkg.formulas) && pkg.formulas.length > 0;
  
  // Vérifier si l'itinéraire existe et est un tableau
  const hasItinerary = Array.isArray(pkg.itinerary) && pkg.itinerary.length > 0;


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* En-tête */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold">{offer.title}</h2>
              <p className="text-gray-600">Valable jusqu'au {validUntil}</p>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-8">
            {/* Détails de l'offre */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-2">Détails de l'offre</h3>
              <p className="text-blue-700 mb-2">{offer.description}</p>
              <div className="flex gap-4 text-sm text-blue-600">
                <div>Réduction : {offer.discount}%</div>
                <div>Participants minimum : {offer.minimumParticipants}</div>
                <div>Statut : {offer.status}</div>
              </div>
            </div>

            {/* Informations de voyage */}
            <div className="grid grid-cols-2 gap-6">
              {/* Colonne gauche */}
              <div className="space-y-4">
                {pkg.departure && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">Ville de départ</p>
                      <p className="text-gray-600">{pkg.departure}</p>
                    </div>
                  </div>
                )}
                
                {pkg.groupSize && (
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">Taille du groupe</p>
                      <p className="text-gray-600">{pkg.groupSize}</p>
                    </div>
                  </div>
                )}
                
                {pkg.departures && Array.isArray(pkg.departures) && pkg.departures.length > 0 ? (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">Dates de départ</p>
                      <ul className="text-gray-600">
                        {pkg.departures.map((dep: any, idx: number) => {
                          
                          // Vérifier le format des dates
                          let departureDate, returnDate;
                          
                          try {
                            // Gérer les timestamps en millisecondes stockés sous forme de chaînes
                            if (typeof dep.departureDate === 'string' && !isNaN(Number(dep.departureDate))) {
                              departureDate = new Date(Number(dep.departureDate));
                            } else if (typeof dep.departureDate === 'string') {
                              departureDate = new Date(dep.departureDate);
                            } else if (typeof dep.departureDate === 'number') {
                              departureDate = new Date(dep.departureDate);
                            } else {
                              departureDate = new Date();
                              console.error("Format de date de départ non reconnu:", dep.departureDate);
                            }
                            
                            if (typeof dep.returnDate === 'string' && !isNaN(Number(dep.returnDate))) {
                              returnDate = new Date(Number(dep.returnDate));
                            } else if (typeof dep.returnDate === 'string') {
                              returnDate = new Date(dep.returnDate);
                            } else if (typeof dep.returnDate === 'number') {
                              returnDate = new Date(dep.returnDate);
                            } else {
                              returnDate = new Date();
                              console.error("Format de date de retour non reconnu:", dep.returnDate);
                            }
                            
                            // Vérifier si les dates sont valides
                            if (isNaN(departureDate.getTime()) || isNaN(returnDate.getTime())) {
                              console.error("Dates invalides:", { departureDate, returnDate });
                              return (
                                <li key={idx} className="text-red-500">
                                  Date invalide
                                </li>
                              );
                            }
                            
                            // Formater les dates
                            const formattedDepartureDate = departureDate.toLocaleDateString('fr-FR');
                            const formattedReturnDate = returnDate.toLocaleDateString('fr-FR');
                            
                            return (
                              <li key={idx}>
                                {formattedDepartureDate} - {formattedReturnDate}
                              </li>
                            );
                          } catch (error) {
                            console.error("Erreur lors du traitement des dates:", error);
                            return (
                              <li key={idx} className="text-red-500">
                                Erreur de format de date
                              </li>
                            );
                          }
                        })}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">Dates de départ</p>
                      <p className="text-gray-600">Aucune date de départ spécifiée</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Colonne droite */}
              <div className="space-y-4">
                {pkg.category && (
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">Catégorie</p>
                      <p className="text-gray-600">{pkg.category}</p>
                    </div>
                  </div>
                )}
                
                {pkg.cities && pkg.cities.length > 0 && (
                  <div className="flex items-start gap-2">
                    <Building className="w-5 h-5 text-gray-500 mt-1" />
                    <div>
                      <p className="font-medium">Villes</p>
                      <p className="text-gray-600">{pkg.cities.join(', ')}</p>
                    </div>
                  </div>
                )}
                
                {pkg.countries && pkg.countries.length > 0 && (
                  <div className="flex items-start gap-2">
                    <Globe className="w-5 h-5 text-gray-500 mt-1" />
                    <div>
                      <p className="font-medium">Pays</p>
                      <p className="text-gray-600">{pkg.countries.join(', ')}</p>
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Image et informations du package */}
            {pkg.image && (
              <div className="relative h-64 rounded-lg overflow-hidden">
                <img 
                  src={getImageUrl(pkg.image)} 
                  alt={pkg.title} 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                  <div className="p-4 text-white">
                    <h3 className="text-xl font-bold">{pkg.title}</h3>
                    <p className="text-sm opacity-90">{pkg.description}</p>
                  </div>
                </div>
            </div>
            )}

            {/* Inclus et exclus */}
            <div className="grid grid-cols-2 gap-6">
              {/* Inclus */}
              {pkg.included && pkg.included.length > 0 && (
              <div>
                <h4 className="font-bold mb-2">Inclus</h4>
                <ul className="space-y-2">
                  {pkg.included.map((item, index) => (
                    <li key={index} className="flex items-center text-gray-600">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              )}

              {/* Exclus */}
              {pkg.excluded && pkg.excluded.length > 0 && (
              <div>
                <h4 className="font-bold mb-2">Non inclus</h4>
                <ul className="space-y-2">
                  {pkg.excluded.map((item, index) => (
                    <li key={index} className="flex items-center text-gray-600">
                      <span className="w-2 h-2 bg-red-500 rounded-full mr-2" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              )}
            </div>

            {/* Formules avec prix réduits */}
            {hasFormulas ? (
            <div>
                <h3 className="text-xl font-bold mb-4">Formules disponibles ({pkg.formulas.length})</h3>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-yellow-800">
                  <strong>Offre spéciale :</strong> Profitez d'une réduction de {offer.discount}% sur tous les tarifs !
                </p>
              </div>
                <div className="grid gap-4">
                  {pkg.formulas.map((formula) => {
                    const originalPrice = formula.perPersonPrice;
                    const discountedPrice = Math.round(originalPrice * (1 - offer.discount / 100));
                    
                    return (
                      <div key={formula.id} className="bg-white border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold text-lg">{formula.name}</h4>
                            {formula.maxPersons && (
                              <p className="text-gray-600">Max {formula.maxPersons} personnes</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="line-through text-gray-400">{originalPrice.toLocaleString('fr-FR')} DA</p>
                            <p className="text-xl font-bold text-green-600">
                              {discountedPrice.toLocaleString('fr-FR')} DA
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800">
                  <strong>Aucune formule disponible</strong> pour ce package.
                </p>
            </div>
            )}

            {/* Itinéraire */}
            {hasItinerary ? (
            <div>
              <h3 className="text-xl font-bold mb-4">Programme détaillé</h3>
                <div className="space-y-6">
                  {pkg.itinerary.map((day) => (
                    <div key={day.dayNumber} className="bg-white border rounded-lg p-4">
                      <h4 className="font-semibold text-lg mb-2">
                        Jour {day.dayNumber} - {day.title}
                      </h4>
                      <p className="text-gray-600 mb-4">{day.description}</p>
                      
                      {day.activities && day.activities.length > 0 && (
                        <div className="mb-4">
                          <h5 className="font-medium mb-2">Activités :</h5>
                          <ul className="list-disc list-inside text-gray-600">
                            {day.activities.map((activity, index) => (
                              <li key={index}>{activity}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="flex justify-between text-sm">
                        <div>
                          <span className="font-medium">Repas inclus :</span>{' '}
                          {[
                            day.meals.breakfast && 'Petit-déjeuner',
                            day.meals.lunch && 'Déjeuner',
                            day.meals.dinner && 'Dîner'
                          ].filter(Boolean).join(', ') || 'Aucun'}
                        </div>
                        <div>
                          <span className="font-medium">Hébergement :</span> {day.accommodation}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-600">
                  <strong>Aucun itinéraire détaillé</strong> disponible pour ce package.
                </p>
              </div>
            )}

            {/* Réservations */}
            {hasBookings ? (
              <div>
                <h3 className="text-xl font-bold mb-4">Réservations ({offer.bookings.length})</h3>
                <div className="space-y-4">
                  {offer.bookings.map((booking, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between">
                        <div>
                          <p className="font-medium">Réservation #{booking.id}</p>
                          <p className="text-sm text-gray-600">
                            {Array.isArray(booking.travelerInfo) && (
                              <span>{booking.travelerInfo.length} voyageur(s)</span>
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">
                            Statut: <span className={`${
                              booking.status === 'CONFIRMED' ? 'text-green-600' :
                              booking.status === 'CANCELLED' ? 'text-red-600' :
                              'text-yellow-600'
                            }`}>
                              {booking.status === 'CONFIRMED' ? 'Confirmée' :
                               booking.status === 'CANCELLED' ? 'Annulée' :
                               'En attente'}
                            </span>
                          </p>
                          <p className="text-xs text-gray-500">
                            Créée le {new Date(booking.createdAt).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      
                      {/* Détails des chambres */}
                      {Array.isArray(booking.roomPreferences) && booking.roomPreferences.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-sm font-medium">Chambres:</p>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {booking.roomPreferences.map((room, idx) => (
                              <span key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded">
                                {room.quantity}x {room.type}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-600">Aucune réservation pour cette offre.</p>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}